import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import { z } from "zod";
import {
  CreateOnboardingTemplateDraftRequestZ,
  CreateOnboardingTemplateRequestZ,
  OnboardingTemplateManagementDetailZ,
  OnboardingTemplateManagementListZ,
  OnboardingTemplateTermsZ,
  OnboardingTemplateValidationResponseZ,
  type CreateOnboardingTemplateDraftRequest,
  type CreateOnboardingTemplateRequest,
  type OnboardingTemplateManagementDetail,
  type OnboardingTemplateManagementListItem,
  type OnboardingTemplateTerms,
  type OnboardingTemplateValidationResponse,
} from "work/onboarding-templates/_schemas/onboarding-template-management-schema";

export type OnboardingTemplateManagementListStatus = "all" | undefined;

export type OnboardingTemplateProblemDetails = {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  code?: string;
  errors?: Array<{ path: string; message: string }>;
};

const OnboardingTemplateProblemDetailsZ = z.object({
  type: z.string().optional(),
  title: z.string().optional(),
  status: z.number().optional(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  code: z.string().optional(),
  errors: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});

const onboardingTemplateCatalogListTag = { type: "OnboardingTemplateCatalog" as const, id: "LIST" };

function idempotencyHeaders(idempotencyKey?: string, expectedRevision?: number): HeadersInit {
  return {
    "Content-Type": "application/json",
    "Idempotency-Key": idempotencyKey ?? crypto.randomUUID(),
    ...(expectedRevision === undefined ? {} : { "If-Match": `"${expectedRevision}"` }),
  };
}

function invalidatesTemplate(templateId: string) {
  return [
    onboardingTemplateCatalogListTag,
    { type: "OnboardingTemplateCatalog" as const, id: undefined },
    { type: "OnboardingTemplateDetail" as const, id: templateId },
  ];
}

export function readOnboardingTemplateProblemDetails(error: unknown): OnboardingTemplateProblemDetails | undefined {
  const candidates = [isRecord(error) && "error" in error ? error.error : undefined, error];
  for (const candidate of candidates) {
    const data = isRecord(candidate) && "data" in candidate ? candidate.data : candidate;
    const parsed = OnboardingTemplateProblemDetailsZ.safeParse(data);
    if (parsed.success) return parsed.data;
  }
  return undefined;
}

export function getOnboardingTemplateProblemCode(error: unknown): string | undefined {
  return readOnboardingTemplateProblemDetails(error)?.code;
}

export function getOnboardingTemplateValidationErrors(error: unknown): Array<{ path: string; message: string }> {
  return readOnboardingTemplateProblemDetails(error)?.errors ?? [];
}

export const onboardingTemplateManagementApi = api.injectEndpoints({
  endpoints: (build) => ({
    getOnboardingTemplateManagementList: build.query<
      OnboardingTemplateManagementListItem[],
      { status?: OnboardingTemplateManagementListStatus } | void
    >({
      query: (args) => ({
        url: "work/onboarding/template-definitions",
        ...(args?.status ? { params: { status: args.status } } : {}),
      }),
      rawResponseSchema: OnboardingTemplateManagementListZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) => [
        onboardingTemplateCatalogListTag,
        ...(result ?? []).map((template) => ({
          type: "OnboardingTemplateDetail" as const,
          id: template.templateId,
        })),
      ],
    }),

    getOnboardingTemplateManagementDetail: build.query<OnboardingTemplateManagementDetail, { templateId: string }>({
      query: ({ templateId }) => `work/onboarding/template-definitions/${encodeURIComponent(templateId)}`,
      rawResponseSchema: OnboardingTemplateManagementDetailZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: (_result, _error, { templateId }) => [{ type: "OnboardingTemplateDetail", id: templateId }],
    }),

    createOnboardingTemplate: build.mutation<OnboardingTemplateManagementDetail, {
      request: CreateOnboardingTemplateRequest;
      idempotencyKey?: string;
    }>({
      query: ({ request, idempotencyKey }) => ({
        url: "work/onboarding/template-definitions",
        method: "POST",
        headers: idempotencyHeaders(idempotencyKey),
        body: CreateOnboardingTemplateRequestZ.parse(request),
      }),
      rawResponseSchema: OnboardingTemplateManagementDetailZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { request }) => invalidatesTemplate(request.templateId),
    }),

    createOnboardingTemplateDraft: build.mutation<OnboardingTemplateManagementDetail, {
      templateId: string;
      request: CreateOnboardingTemplateDraftRequest;
      idempotencyKey?: string;
    }>({
      query: ({ templateId, request, idempotencyKey }) => ({
        url: `work/onboarding/template-definitions/${encodeURIComponent(templateId)}/drafts`,
        method: "POST",
        headers: idempotencyHeaders(idempotencyKey),
        body: CreateOnboardingTemplateDraftRequestZ.parse(request),
      }),
      rawResponseSchema: OnboardingTemplateManagementDetailZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { templateId }) => invalidatesTemplate(templateId),
    }),

    editOnboardingTemplateDraft: build.mutation<OnboardingTemplateManagementDetail, {
      templateId: string;
      version: string;
      request: OnboardingTemplateTerms;
      expectedRevision: number;
      idempotencyKey?: string;
    }>({
      query: ({ templateId, version, request, expectedRevision, idempotencyKey }) => ({
        url: `work/onboarding/template-definitions/${encodeURIComponent(templateId)}/drafts/${encodeURIComponent(version)}`,
        method: "PUT",
        headers: idempotencyHeaders(idempotencyKey, expectedRevision),
        body: OnboardingTemplateTermsZ.parse(request),
      }),
      rawResponseSchema: OnboardingTemplateManagementDetailZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { templateId }) => invalidatesTemplate(templateId),
    }),

    validateOnboardingTemplateDraft: build.mutation<OnboardingTemplateValidationResponse, {
      templateId: string;
      version: string;
      idempotencyKey?: string;
    }>({
      query: ({ templateId, version, idempotencyKey }) => ({
        url: `work/onboarding/template-definitions/${encodeURIComponent(templateId)}/drafts/${encodeURIComponent(version)}/validate`,
        method: "POST",
        headers: idempotencyHeaders(idempotencyKey),
      }),
      rawResponseSchema: OnboardingTemplateValidationResponseZ,
      onSchemaFailure: logSchemaFailure,
    }),

    publishOnboardingTemplateDraft: build.mutation<OnboardingTemplateManagementDetail, {
      templateId: string;
      version: string;
      expectedRevision: number;
      idempotencyKey?: string;
    }>({
      query: ({ templateId, version, expectedRevision, idempotencyKey }) => ({
        url: `work/onboarding/template-definitions/${encodeURIComponent(templateId)}/drafts/${encodeURIComponent(version)}/publish`,
        method: "POST",
        headers: idempotencyHeaders(idempotencyKey, expectedRevision),
      }),
      rawResponseSchema: OnboardingTemplateManagementDetailZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { templateId }) => invalidatesTemplate(templateId),
    }),

    setOnboardingTemplateDefault: build.mutation<OnboardingTemplateManagementListItem[], {
      templateId: string;
      idempotencyKey?: string;
    }>({
      query: ({ templateId, idempotencyKey }) => ({
        url: `work/onboarding/template-definitions/${encodeURIComponent(templateId)}/set-default`,
        method: "POST",
        headers: idempotencyHeaders(idempotencyKey),
      }),
      rawResponseSchema: OnboardingTemplateManagementListZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { templateId }) => [
        ...invalidatesTemplate(templateId),
        { type: "OnboardingTemplateDetail" as const, id: undefined },
      ],
    }),

    retireOnboardingTemplate: build.mutation<OnboardingTemplateManagementDetail, {
      templateId: string;
      idempotencyKey?: string;
    }>({
      query: ({ templateId, idempotencyKey }) => ({
        url: `work/onboarding/template-definitions/${encodeURIComponent(templateId)}/retire`,
        method: "POST",
        headers: idempotencyHeaders(idempotencyKey),
      }),
      rawResponseSchema: OnboardingTemplateManagementDetailZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { templateId }) => invalidatesTemplate(templateId),
    }),

    reactivateOnboardingTemplate: build.mutation<OnboardingTemplateManagementDetail, {
      templateId: string;
      idempotencyKey?: string;
    }>({
      query: ({ templateId, idempotencyKey }) => ({
        url: `work/onboarding/template-definitions/${encodeURIComponent(templateId)}/reactivate`,
        method: "POST",
        headers: idempotencyHeaders(idempotencyKey),
      }),
      rawResponseSchema: OnboardingTemplateManagementDetailZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { templateId }) => invalidatesTemplate(templateId),
    }),
  }),
});

export const {
  useGetOnboardingTemplateManagementListQuery,
  useLazyGetOnboardingTemplateManagementListQuery,
  useGetOnboardingTemplateManagementDetailQuery,
  useLazyGetOnboardingTemplateManagementDetailQuery,
  useCreateOnboardingTemplateMutation,
  useCreateOnboardingTemplateDraftMutation,
  useEditOnboardingTemplateDraftMutation,
  useValidateOnboardingTemplateDraftMutation,
  usePublishOnboardingTemplateDraftMutation,
  useSetOnboardingTemplateDefaultMutation,
  useRetireOnboardingTemplateMutation,
  useReactivateOnboardingTemplateMutation,
} = onboardingTemplateManagementApi;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
